import { type CartItem, type HarvestProduct } from "../../utils/types";

/** Returns an error message when cart exceeds current harvest stock. */
export function getCartStockError(
  cartItems: CartItem[],
  harvestProducts: HarvestProduct[],
): string | null {
  const unavailableProducts = [];

  for (const cartItem of cartItems) {
    if (cartItem.quantity <= 0) continue;

    const harvestProduct = harvestProducts.find(
      (p) => p.name === cartItem.productName,
    );
    const available = Math.max(0, harvestProduct?.quantity ?? 0);

    if (cartItem.quantity > available) {
      unavailableProducts.push(cartItem.productName);
    }
  }

  if (unavailableProducts.length > 0) {
    return `You've surpased the available stock for the following products: ${unavailableProducts.join(", ")}.`;
  }

  return null;
}

function getItemTotalPrice(
  harvestProduct: HarvestProduct | undefined,
  amount: number,
): number {
  const unitPrice = getItemUnitPrice(harvestProduct);
  return unitPrice * amount;
}

function getItemUnitPrice(harvestProduct: HarvestProduct | undefined): number {
  if (!harvestProduct?.price) return 0;
  if (harvestProduct.discount && harvestProduct.discount > 0) {
    const discounted =
      harvestProduct.price * (1 - harvestProduct.discount / 100);
    return Math.round(discounted * 10) / 10;
  }
  return harvestProduct.price;
}

export const getUpdatedCart = (
  currentCart: CartItem[],
  harvestProducts: HarvestProduct[],
  productName: string,
  productAmount: string,
) => {
  const parsedAmount = parseFloat(productAmount);
  const amount = Number.isFinite(parsedAmount) ? parsedAmount : 0;
  const harvestProduct = harvestProducts.find((p) => p.name === productName);
  const unitPrice = getItemUnitPrice(harvestProduct);
  const totalPrice = getItemTotalPrice(harvestProduct, amount);
  const productCartIndex = currentCart.findIndex(
    (item) => item.productName === productName,
  );
  const isAlreadyInCart = productCartIndex >= 0;

  if (isAlreadyInCart) {
    const updated = [...currentCart];
    updated[productCartIndex] = {
      ...updated[productCartIndex],
      unitPrice,
      quantity: amount,
      totalPrice,
    };
    return updated;
  }

  return [
    ...currentCart,
    {
      productName: productName,
      unitPrice,
      quantity: amount,
      totalPrice,
    },
  ];
};

export const getShippingPrice = (
  subtotal: number,
  freeDeliveryThreshold: number,
  businessDeliveryPrice: number,
) => {
  if (subtotal >= freeDeliveryThreshold) return 0;
  return businessDeliveryPrice;
};
